import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import { SiteStatsWithKeywords } from "~/components/dashboard/types";
import { routes } from "~/router";

import {
  Keyword,
  KeywordHistoricalRanking,
  KeywordRanking,
  Keywords as KeywordsType,
} from "../../../../api/Api";
import { Icon } from "../../../../assets/icons/Icon";
import { formatNumber, shortenUrl } from "../../../../helpers/helpers";
import NoData from "../../../containers/NoData";
import Card from "../../../ui/card/Card";
import DataTable from "../../../ui/dataTable/DataTable";
import Spinner from "../../../ui/spinner/Spinner";
import styles from "./Keywords.module.scss";

const topKeywordsLabels = [
  { label: "Top 5", key: "top_5" },
  { label: "Top 10", key: "top_10" },
  { label: "Top 25", key: "top_25" },
  { label: "Top 100", key: "top_100" },
] as const;

type KeywordsTypeProps = {
  siteStats: SiteStatsWithKeywords | undefined;
  isSiteStatsLoading: boolean;
  keywordsHistoricalRankingDetail: KeywordHistoricalRanking | undefined;
  isKeywordsHistoricalRankingDetailLoading: boolean;
  keywordsRankingDetail: KeywordRanking | undefined;
  isKeywordsRankingDetailLoading: boolean;
  topKeywords: KeywordsType | undefined;
  isTopKeywordsLoading: boolean;
};

const topKeywordsColumns: ColumnDef<Keyword>[] = [
  {
    accessorKey: "name",
    header: "Keyword",
  },
  {
    accessorKey: "now_pos",
    header: "Rank",
  },
  {
    accessorKey: "ranking_url",
    header: "URL",
    cell: (info) => {
      const value = info.getValue() as string | undefined;
      if (!value) return "";
      let url: URL | null = null;
      try {
        url = new URL(value);
      } catch {
        // invalid URL
      }

      return (
        <a
          target="_blank"
          href={value}
          className={styles.tableLink}
          rel="noopener noreferrer"
        >
          <Icon iconName="LinkIcon" width="1rem" height="1rem" />
          <span>{url ? shortenUrl(url.pathname) : value}</span>
        </a>
      );
    },
  },
];

const Keywords = ({
  siteStats,
  isSiteStatsLoading,
  // keywordsHistoricalRankingDetail,
  isKeywordsRankingDetailLoading,
  isKeywordsHistoricalRankingDetailLoading,
  keywordsRankingDetail,
  topKeywords,
  isTopKeywordsLoading,
}: KeywordsTypeProps) => {
  const { id } = useParams();
  const data = useMemo(() => topKeywords ?? [], [topKeywords]);

  if (!id) {
    return null;
  }

  return (
    <Card
      title="Keywords rankings"
      button={
        <Link
          to={routes.site.keywords.list({ id })}
          className={styles.linkButton}
        >
          <span>
            <Icon iconName="ChartBarIcon" />
            View Keywords
          </span>
        </Link>
      }
    >
      <div className={styles.analyticsKeywords}>
        <div className={styles.rankings}>
          {isSiteStatsLoading && <Spinner />}
          {topKeywordsLabels?.map((item) => {
            const percent = siteStats?.keywords[`${item.key}_percent`] || 0;

            return (
              <div key={item.label} className={styles.rankingsBlock}>
                <div className={styles.rankingsLabel}>{item.label}</div>
                <div className={styles.rankingsValues}>
                  <div className={styles.rankingsRank}>
                    {formatNumber(siteStats?.keywords?.[item.key])}
                  </div>
                  {percent > 0 && (
                    <div className={styles.percentUp}>
                      +{Math.ceil(percent)}%
                      <Icon iconName="PercentUpIcon" />
                    </div>
                  )}
                  {percent < 0 && (
                    <div className={styles.percentDown}>
                      -{Math.ceil(percent)}%
                      <Icon iconName="PercentDownIcon" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className={styles.container}>
          <div className={styles.containerTitle}>Estimated Values</div>
          <div className={styles.containerSubtitle}>
            Keywords rankings to date
          </div>
          {(isKeywordsHistoricalRankingDetailLoading ||
            isKeywordsRankingDetailLoading) && <Spinner />}
          {!keywordsRankingDetail?.day && (
            <NoData title="No Estimated Values" />
          )}
        </div>
        <div className={styles.container}>
          <div className={styles.containerTitle}>Top keywords</div>
          <div className={styles.containerTable}>
            {(() => {
              switch (true) {
                case isTopKeywordsLoading:
                  return <Spinner />;
                case !topKeywords || topKeywords.length === 0:
                  return <NoData title="No Estimated Values" />;
                default:
                  return (
                    <DataTable
                      columns={topKeywordsColumns}
                      data={data}
                      isLoading={false}
                      tableLayout="auto"
                    />
                  );
              }
            })()}
          </div>
        </div>
      </div>
    </Card>
  );
};
export default Keywords;
